import { format } from "date-fns";
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { ContactTypes } from "pages/enums";

const toCamelCase = (str) => {
  return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

// Convert a CSS style string to a camelCase style object
export const convertStyleStringToObject = (styleString) => {
  return styleString.split(";").reduce((acc, style) => {
    if (style.trim()) {
      const [key, value] = style.split(":");
      acc[toCamelCase(key.trim())] = value.trim();
    }
    return acc;
  }, {});
};

export const formatUTCMidnightToSameDay = (timestamp, formatString = "MM/dd/yyyy") => {
  //return format(utcToZonedTime(timestamp, 'UTC'), formatString, { timeZone: 'UTC' })
  const date = new Date(timestamp); // handles ISO string or Date object
  return formatInTimeZone(date, "UTC", formatString)
}

export const formatDateOnlyInUtc = (dateValue) => {
  if(!dateValue || dateValue === "") {
    return null;
  }

  const date = new Date(dateValue);
  return date.toLocaleDateString('en-US', { 
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit'
  });
};

export const formatToLocalDateFrom = (utcDateValue, formatString = "MM.dd.yyyy") => {
  if(!utcDateValue || utcDateValue === "") {
    return null;
  }
  const utcDate = new Date(utcDateValue);
  return format(utcDate, formatString);
};

export const renderTypeContact = (typeContact) => {
  switch (typeContact) {
    case ContactTypes.CPNW_ADMIN:
      return 'CPNW Clinical Passport';
    case ContactTypes.HEALTHCARE:
      return 'Healthcare';
    case ContactTypes.EDUCATION:
      return 'Education';
    default:
      return '';
  }
};

export const renderReviewerRole = (reviewerRole) => {
  switch ((reviewerRole || '').toUpperCase()) {
    case 'CPNW-REVIEWER':
      return 'CPNW Reviewer';
    case 'EDUCATION':
      return 'Education';
    case 'FACULTY':
      return 'Faculty';
    case 'HEALTHCARE':
      return 'Healthcare';
    default:
      return '';
  }
};

/**
 * Formats a given date string into a human-friendly format.
 *
 * - If the date is today: returns "Today hh:mmam/pm"
 * - If the date is yesterday: returns "Yesterday hh:mmam/pm"
 * - Otherwise: returns "MM/DD/YYYY hh:mmam/pm"
 *
 * Notes:
 * - Uses 12-hour time format by default (e.g., 11:05pm).
 * - Strips time to compare only the date portions (ignores hours/minutes).
 * - Useful for chat UIs, notifications, or message timestamps.
 *
 * @param {string} dateString - The ISO date string to format.
 * @returns {string} A formatted date/time string.
 */
export const formatMessageDate = (dateString) => {
  // Toggle between 24-hour and 12-hour time format
  const hourFormat = true;

  // Parse input date string and get today's date
  const inputDate = new Date(dateString);
  const today = new Date();

  // Utility function to remove time from a date (set time to midnight)
  const stripTime = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  // Format the time portion of the input date
  const returnTime = () => {
    let time = inputDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: hourFormat,
    });

    // Clean up AM/PM spacing and case
    return hourFormat
      ? time.replace("AM", "am").replace("PM", "pm").replace(" ", "")
      : time;
  };

  // Strip time from both input and current dates for comparison
  const input = stripTime(inputDate);
  const current = stripTime(today);

  // Calculate difference in milliseconds and convert to days
  const diffTime = current - input;
  const diffDays = diffTime / 86400000; // 1000 ms * 60 sec * 60 min * 24 hrs

  // Return formatted date string based on difference
  if (diffDays === 0) {
    return "Today " + returnTime();
  } else if (diffDays === 1) {
    return "Yesterday " + returnTime();
  } else {
    // Fallback: full date + time for older messages
    return (
      inputDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }) +
      " " +
      returnTime()
    );
  }
};

// Non-license requirements edit only Submissions[0]; stale older rows must not count.
export const requirementHasDeclination = (requirement) => {
  const submissions = requirement.Submissions || [];
  return requirement.Category?.HasLicense
    ? submissions.some((s) => s.Declination)
    : !!submissions[0]?.Declination;
};

export const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    
    const digits = phone.replace(/\D/g, '');
    
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    return phone;
};
